import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { UntypedFormControl, ReactiveFormsModule } from '@angular/forms';
import { debounceTime } from 'rxjs/operators';
import { Table, ApiClientConstant, RequestQueryPayload } from '@cureskin/api-client';
import { ConnectionService } from '../../../../services/connection-service';
import { AppConfig } from '../../../app.config';
import { Broadcaster } from '../../../../components/broadcaster';
import {
  SimilarLanguageStringSuggestionComponent,
} from '../../../../components/language-string/similar-language-string-suggestion/similar_language_string_suggestion.component';
import { MatDialog } from '@angular/material/dialog';

@Component({
  selector: 'language-string-dialog',
  templateUrl: './language-string-dialog.html',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule
  ]
})
export class LanguageStringDialogComponent implements OnInit {
  searchControl: UntypedFormControl = new UntypedFormControl();
  options: Array<{ display: string; value: string; parseValue: any }> = [];
  tags: Array<any> = [];
  similarLanguageStrings: Array<any> = [];
  currentUser: any;
  selectedValue: string = '';
  showSearch: boolean = true;
  currentLanguageString: any;

  constructor(
    private dialogRef: MatDialogRef<LanguageStringDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: {
      currentLanguageStringId?: string;
      tags?: Array<string>;
      searchValue?: string;
    },
    private conn: ConnectionService,
    public appConfig: AppConfig,
    private broadcaster: Broadcaster,
    private dialog: MatDialog
  ) {
    if (data?.searchValue) {
      this.searchControl.setValue(data.searchValue);
    }
  }

  async ngOnInit(): Promise<void> {
    this.currentUser = this.conn.getCurrentUser();
    if (this.data?.tags) {
      await this.fetchTags(this.data.tags);
    }
    if (this.data?.currentLanguageStringId) {
      this.currentLanguageString = await this.conn.getLanguageString(this.data.currentLanguageStringId);
      this.selectedValue = this.currentLanguageString?.id;
    }
    this.searchControl.valueChanges
      .pipe(debounceTime(300))
      .subscribe((newValue: string) => this.updateOptions());
    await this.updateOptions();
  }

  async fetchTags(tags: Array<string>): Promise<any> {
    this.tags = await this.conn.fetchLanguageStringTags({ tags });
    await this.updateOptions();
  }

  async updateOptions(): Promise<void> {
    const payload: RequestQueryPayload<Table.LanguageString> = {
      where: {},
      project: ['en'],
      limit: 100,
    };
    if (this.tags?.length) {
      payload.where.tags = this.tags;
    }
    if (this.searchControl.value || this.currentLanguageString?.get('en')) {
      payload.where.en = { $regex: this.searchControl.value || this.currentLanguageString?.get('en'), $options: 'i' };
    }
    const languageStrings = await this.conn.findLanguageStrings(payload);
    this.options = languageStrings.map((languageString: any) => ({
      display: languageString.get(ApiClientConstant.LanguageString.EN),
      value: languageString.id,
      parseValue: languageString,
    }));
    if (this.currentLanguageString?.id
      && this.options.every((each) => each.value !== this.currentLanguageString.id)) {
      this.options.unshift({
        display: this.currentLanguageString.get(ApiClientConstant.LanguageString.EN),
        value: this.currentLanguageString.id,
        parseValue: this.currentLanguageString,
      });
    }
  }

  onSelectionChange(): void {
    // Selection handled by select option
  }

  async findSimilarLanguageString(searchText: string): Promise<any> {
    let regex = `^${searchText}$`;
    if (['!', '?', '.'].includes(searchText.charAt(searchText.length - 1))) {
      regex = `^${searchText}`;
    }
    const payload: RequestQueryPayload<Table.LanguageString> = {
      where: {},
      include: ['tags'],
    };
    if (searchText) {
      payload.where.en = { $regex: regex, $options: 'i' };
    }
    try {
      this.similarLanguageStrings = await this.conn.findLanguageStrings(payload);
    } catch (error: any) {
      this.broadcaster.broadcast('NOTIFY', {
        message: error.message,
        type: this.appConfig.Shared.Toast.Type.ERROR,
      });
    }
  }

  async addLanguageString(): Promise<any> {
    if (!this.searchControl.value) return;
    const languageStringObj = new Table.LanguageString();
    languageStringObj.set('tags', this.tags);
    languageStringObj.set('en', this.searchControl.value);

    await this.findSimilarLanguageString(this.searchControl.value);
    if (this.similarLanguageStrings.length) {
      const dialogRef = this.dialog
        .open(SimilarLanguageStringSuggestionComponent, { data: { languageStrings: this.similarLanguageStrings } });
      dialogRef.afterClosed().subscribe(async (similarLanguageString: any): Promise<any> => {
        if (!similarLanguageString) {
          await this.createLanguageString(languageStringObj);
          return;
        }
        if (similarLanguageString === this.appConfig.Shared.Actions.close) return '';
        languageStringObj.get('tags').forEach((tag: any): void => {
          const isTagPresent = similarLanguageString.get('tags').find((value: any): any => tag.get('name') === value.get('name'));
          if (!isTagPresent) {
            similarLanguageString.get('tags').push(tag);
          }
        });
        await similarLanguageString.save();
        this.selectedValue = similarLanguageString.id;
        this.currentLanguageString = similarLanguageString;
        await this.updateOptions();
        return similarLanguageString;
      });
    } else {
      await this.createLanguageString(languageStringObj);
    }
  }

  async createLanguageString(languageStringObj: any): Promise<void> {
    if (!languageStringObj.get('createdBy')) {
      languageStringObj.set('createdBy', this.currentUser);
    }
    await languageStringObj.save();
    this.selectedValue = languageStringObj.id;
    this.currentLanguageString = languageStringObj;
    await this.updateOptions();
  }

  save(): void {
    const selectedOption = this.options.find(option => option.value === this.selectedValue);
    if (selectedOption) {
      this.dialogRef.close(selectedOption.parseValue);
    } else {
      this.dialogRef.close();
    }
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
